import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation, Outlet } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import api from "@/lib/api";
import { FloatingWhatsApp } from "@/components/WhatsApp";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, CalendarCheck, Bus, Users, FileText, BarChart3, Truck,
  MapPin, Navigation, User, Settings, LogOut, ChevronRight, Building2,
  Wallet, ClipboardList, Route, Menu, Globe2, Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const roleNav = {
  admin: [
    { to: "/admin", icon: LayoutDashboard, k: "dashboard" },
    { to: "/admin/bookings", icon: CalendarCheck, k: "booking" },
    { to: "/admin/dispatch", icon: Route, k: "dispatch" },
    { to: "/admin/fleet", icon: Bus, k: "fleet" },
    { to: "/admin/drivers", icon: Truck, k: "driver" },
    { to: "/admin/customers", icon: Users, k: "customers" },
    { to: "/admin/partners", icon: Building2, k: "partner" },
    { to: "/admin/finance", icon: Wallet, k: "finance" },
    { to: "/admin/reports", icon: BarChart3, k: "reports" },
    { to: "/admin/crm", icon: Sparkles, k: "crm" },
    { to: "/admin/settings", icon: Settings, k: "settings" },
  ],
  partner: [
    { to: "/partner", icon: LayoutDashboard, k: "dashboard" },
    { to: "/partner/new", icon: CalendarCheck, k: "new_booking" },
    { to: "/partner/bookings", icon: ClipboardList, k: "my_booking" },
    { to: "/partner/customers", icon: Users, k: "customers" },
    { to: "/partner/invoice", icon: FileText, k: "invoice" },
    { to: "/partner/reports", icon: BarChart3, k: "reports" },
  ],
  driver: [
    { to: "/driver", icon: MapPin, k: "today_trip" },
    { to: "/driver/navigation", icon: Navigation, k: "navigation" },
    { to: "/driver/history", icon: ClipboardList, k: "trip_history" },
  ],
  customer: [
    { to: "/customer", icon: LayoutDashboard, k: "dashboard" },
    { to: "/customer/bookings", icon: ClipboardList, k: "my_booking" },
    { to: "/customer/tracking", icon: MapPin, k: "live_tracking" },
    { to: "/customer/profile", icon: User, k: "profile" },
  ],
};

export function LanguageSwitcher() {
  const { lang, setLang } = useApp();
  return (
    <div className="inline-flex items-center rounded-full bg-slate-100 border border-slate-200 p-1" data-testid="lang-switch">
      {["ID", "EN"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}`}
          className={cn("px-3 py-1 text-xs font-semibold rounded-full transition",
            lang === l ? "bg-emerald-600 text-white shadow" : "text-slate-600 hover:text-slate-900")}>
          {l}
        </button>
      ))}
    </div>
  );
}

export function PublicNav() {
  const { t, user } = useApp();
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-40 backdrop-blur-lg bg-white/85 border-b border-slate-200">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 py-4">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-link">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white font-black">A</div>
          <span className="font-extrabold text-lg tracking-tight">Abdan<span className="text-emerald-600">Pro</span></span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-700">
          <NavLink to="/" data-testid="nav-home" className={({ isActive }) => isActive ? "text-emerald-700" : "hover:text-emerald-700"}>{t("home")}</NavLink>
          <NavLink to="/booking" data-testid="nav-booking" className={({ isActive }) => isActive ? "text-emerald-700" : "hover:text-emerald-700"}>{t("booking")}</NavLink>
          <NavLink to="/cek-booking" data-testid="nav-cek" className={({ isActive }) => isActive ? "text-emerald-700" : "hover:text-emerald-700"}>{t("cek_booking")}</NavLink>
          <NavLink to="/armada" data-testid="nav-armada" className={({ isActive }) => isActive ? "text-emerald-700" : "hover:text-emerald-700"}>{t("armada")}</NavLink>
          <NavLink to="/tentang" data-testid="nav-about" className={({ isActive }) => isActive ? "text-emerald-700" : "hover:text-emerald-700"}>{t("about_us")}</NavLink>
          <NavLink to="/layanan-lainnya" data-testid="nav-other" className={({ isActive }) => isActive ? "text-emerald-700" : "hover:text-emerald-700"}>{t("other_services")}</NavLink>
          <NavLink to="/contact" data-testid="nav-contact" className={({ isActive }) => isActive ? "text-emerald-700" : "hover:text-emerald-700"}>{t("contact")}</NavLink>
        </nav>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          {user ? (
            <Button onClick={() => nav(`/${user.role}`)} className="bg-emerald-600 hover:bg-emerald-700" data-testid="nav-dashboard">
              {t("dashboard")}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={() => nav("/login")} data-testid="nav-login">{t("login")}</Button>
              <Button onClick={() => nav("/register")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="nav-register">{t("register")}</Button>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

export function PublicLayout() {
  const [phone, setPhone] = useState("");
  useEffect(() => { api.get("/public/catalog").then((r) => setPhone(r.data.settings.whatsapp)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen">
      <PublicNav />
      <main><Outlet /></main>
      <FloatingWhatsApp phone={phone} />
      <footer className="border-t border-slate-200 bg-slate-900 text-slate-300 mt-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white font-black">A</div>
              <span className="font-extrabold text-lg text-white">Abdan<span className="text-emerald-400">Pro</span></span>
            </div>
            <p className="text-sm text-slate-400">Solusi transportasi cerdas untuk perjalanan lintas Arab Saudi.</p>
          </div>
          <div><h4 className="text-white font-semibold mb-3">Layanan</h4><ul className="text-sm space-y-2"><li>Bus Pariwisata</li><li>Van VIP</li><li>Sedan Executive</li><li>Umrah & Haji</li></ul></div>
          <div><h4 className="text-white font-semibold mb-3">Perusahaan</h4><ul className="text-sm space-y-2"><li><Link to="/tentang">Tentang Kami</Link></li><li><Link to="/layanan-lainnya">Layanan Lainnya</Link></li><li><Link to="/armada">Armada</Link></li><li><Link to="/cek-booking">Cek Booking</Link></li><li><Link to="/contact">Kontak</Link></li></ul></div>
          <div><h4 className="text-white font-semibold mb-3">Kontak</h4><ul className="text-sm space-y-2"><li>+62 812 7422 6771</li><li>abdanpro1@gmail.com</li><li>Makkah, KSA</li></ul></div>
        </div>
        <div className="border-t border-slate-800 py-4 text-center text-xs text-slate-500">© 2026 Abdan Pro. All rights reserved.</div>
      </footer>
    </div>
  );
}

export function AppLayout({ role }) {
  const { t, user, logout, lang } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const items = roleNav[role] || [];

  if (user === undefined) {
    return <div className="min-h-screen grid place-items-center text-slate-500">Loading...</div>;
  }
  if (!user) { nav("/login"); return null; }
  if (user.role !== role) { nav(`/${user.role}`); return null; }

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className="w-64 hidden md:flex flex-col bg-slate-900 text-slate-200 sticky top-0 h-screen" data-testid="side-nav">
        <Link to="/" className="flex items-center gap-2 px-6 py-6 border-b border-slate-800">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white font-black">A</div>
          <div>
            <div className="font-extrabold text-white leading-tight">Abdan<span className="text-emerald-400">Pro</span></div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{role} Portal</div>
          </div>
        </Link>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {items.map((it) => {
            const Icon = it.icon;
            const active = loc.pathname === it.to || (it.to !== `/${role}` && loc.pathname.startsWith(it.to));
            return (
              <NavLink key={it.to} to={it.to} end={it.to === `/${role}`} data-testid={`side-${it.k}`}
                className={cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition",
                  active ? "bg-emerald-600 text-white shadow" : "text-slate-300 hover:bg-slate-800 hover:text-white")}>
                <Icon className="w-4 h-4" />
                <span>{t(it.k)}</span>
                {active && <ChevronRight className="w-4 h-4 ml-auto" />}
              </NavLink>
            );
          })}
        </nav>
        <div className="px-3 pb-4 space-y-2">
          <LanguageSwitcher />
          <Button variant="ghost" onClick={async () => { await logout(); nav("/"); }}
                   className="w-full justify-start text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="btn-logout">
            <LogOut className="w-4 h-4 mr-2" /> {t("logout")}
          </Button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-white border-b border-slate-200 px-4 md:px-8 py-3 flex items-center justify-between sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger className="md:hidden p-2" data-testid="mobile-menu-trigger"><Menu /></DropdownMenuTrigger>
              <DropdownMenuContent className="bg-white">
                {items.map((it) => (
                  <DropdownMenuItem key={it.to} onClick={() => nav(it.to)}>{t(it.k)}</DropdownMenuItem>
                ))}
                <DropdownMenuItem onClick={async () => { await logout(); nav("/"); }}>{t("logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-400">{t("welcome")}</div>
              <div className="font-bold text-slate-900">{user.name}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Globe2 className="w-4 h-4 text-slate-400" />
            <span className="text-xs text-slate-500 hidden md:inline">{lang} · SAR</span>
            <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center font-bold text-sm">
              {user.name?.[0]?.toUpperCase() || "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8"><Outlet /></main>
      </div>
    </div>
  );
}
